import { SetStateAction, useState, type FormEvent } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import PageHeader from "@/pages/_shared/PageHeader";
import { FadeUp, Stagger, StaggerItem } from "@/pages/_shared/anim";
import ReCAPTCHA from "react-google-recaptcha";
import BrochurePdf from "@/assets/2026 UPlift catalog_Lite.pdf";

const inquiryTypes = [
  { value: "spare-part", label: "Request a Spare Part" },
  { value: "technical", label: "Technical Support" },
  { value: "pcb-repair", label: "PCB Repair" },
  { value: "installation", label: "Supply & Installation" },
  { value: "maintenance", label: "Maintenance & Upgradation" },
  { value: "testing", label: "Testing & Commissioning" },
  { value: "programming", label: "Programming & Configuration" },
  { value: "survey", label: "Conditional Survey" },
  { value: "inspection", label: "Safety Inspection" },
  { value: "general", label: "General Enquiry" },
];

const SITE_KEY = "6LcqH2EtAAAAAH4kWU7dB2gHeTrv41IKgLIzYP26";

const contactOptions = [
  {
    icon: "ri-phone-line",
    title: "TECHNICAL SUPPORT",
    value: "050 566 43 36",
    href: "tel:0505664336",
  },
  {
    icon: "ri-mail-send-line",
    title: "SPARE PART ENQUIRY",
    value: "upliftone1@gmail.com",
    href: "mailto:upliftone1@gmail.com",
  },
  {
    icon: "ri-file-list-3-line",
    title: "REQUEST A QUOTE",
    value: "Use enquiry form",
    href: "#contact-form",
  },
  {
    icon: "ri-map-pin-line",
    title: "LOCATION",
    value: "Abu Dhabi – UAE",
    href: "#",
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
    "idle"
  );
  const [error, setError] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
      if (!captchaToken) {
        setError("Please complete the CAPTCHA.");
        return;
      }
    const form = e.currentTarget;
    const data = new FormData(form);
     data.append("captcha", captchaToken);

    const message = (data.get("message") as string) || "";
    if (message.length > 500) {
      setError("Please keep your message under 500 characters.");
      return;
    }

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

   try {
      setStatus("loading");
      const res = await fetch("/api/send-mail.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      // const responseText = await res.text();
      // if (!res.ok) throw new Error("submit failed");
      // const parsed = JSON.parse(responseText);
      // if (parsed.code !== "OK") throw new Error(parsed.meta?.message || "submit failed");
      const responseText = await res.text();

        console.log("Status :", res.status);
        console.log("Response :", responseText);

        if (!res.ok) {
            throw new Error(responseText);
        }

        const parsed = JSON.parse(responseText);

        if (parsed.code !== "OK") {
            throw new Error(parsed.meta?.message);
        }
      setStatus("success");
      form.reset();
      setCaptchaToken(null);
      setCharCount(0);
    // } catch (err) {
    //   setStatus("error");
    //   setError("Something went wrong. Please try again or call us directly at 050 566 43 36.");
    // }
       } catch (err: any) {
    console.error(err);

    setStatus("error");

    setError(err.message || "Something went wrong.");
}
  };

  return (
    <div className="min-w-0 bg-crest-cream">
      <Navbar />
      <main>
        <PageHeader
          eyebrow="Start the conversation"
          title={
            <>
              Tell Us What You Need.
              <br />
              <span className="text-white">We'll Help Find the Solution.</span>
            </>
          }
          description="Whether you're looking for a spare part, technical support, PCB repair, elevator installation, maintenance, modernization, programming, inspection, or testing, share your requirement with Uplift One."
          crumbs={[
            { label: "Home", to: "/" },
            { label: "Contact" },
          ]}
        />

        {/* Contact options tiles */}
        <section className="bg-crest-cream pt-12 pb-4">
          <Stagger className="px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" stagger={0.08}>
            {contactOptions.map((a) => (
              <StaggerItem key={a.title}>
                <a
                  href={a.href}
                  className="h-full bg-white border border-black/5 hover:border-crest-orange transition-colors rounded-lg p-5 flex items-start gap-4 cursor-pointer"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-crest-deep text-crest-orange shrink-0">
                    <i className={`${a.icon} text-xl`}></i>
                  </span>
                  <div>
                    <div className="text-xs text-crest-deep/55 uppercase tracking-wider">
                      {a.title}
                    </div>
                    <div className="font-display text-lg text-crest-deep mt-1">
                      {a.value}
                    </div>
                  </div>
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        </section>

        {/* Main form */}
        <section className="bg-crest-cream py-12 md:py-16" id="contact-form">
          <div className="px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <FadeUp className="lg:col-span-8">
                <form
                  id="uplift-contact-page"
                  data-readdy-form
                  onSubmit={onSubmit}
                  className="bg-white rounded-xl p-6 md:p-10 border border-black/5"
                >
                  <div className="flex items-center justify-between mb-7">
                    <h2 className="font-display text-2xl md:text-3xl text-crest-deep">
                      Detailed Enquiry Form
                    </h2>
                    <span className="hidden md:inline-flex items-center gap-2 text-xs text-crest-deep/60">
                      <span className="w-2 h-2 rounded-full bg-crest-orange animate-pulse" />
                      Response within 1 business day
                    </span>
                  </div>

                  {status === "success" ? (
                    <div className="flex flex-col items-center text-center py-12">
                      <span className="w-16 h-16 flex items-center justify-center rounded-full bg-crest-orange text-white mb-5">
                        <i className="ri-check-line text-3xl"></i>
                      </span>
                      <h3 className="font-display text-3xl text-crest-deep">
                        Requirement submitted!
                      </h3>
                      <p className="text-crest-deep/70 mt-3 max-w-md">
                        Our technical team will review your requirement and respond within one business day.
                      </p>
                      <button
                        type="button"
                        onClick={() => setStatus("idle")}
                        className="mt-6 inline-flex items-center gap-2 bg-crest-orange text-white px-6 py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
                      >
                        Submit another enquiry
                      </button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                          Inquiry type *
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                          {inquiryTypes.map((t, i) => (
                            <label
                              key={t.value}
                              className="flex items-center gap-2 px-4 py-2.5 bg-crest-cream rounded-md text-sm text-crest-deep cursor-pointer border border-transparent has-[:checked]:border-crest-deep has-[:checked]:bg-crest-orange"
                            >
                              <input
                                type="radio"
                                name="inquiry_type"
                                value={t.value}
                                defaultChecked={i === 0}
                                required
                                className="accent-crest-deep"
                              />
                              {t.label}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Full name *
                          </label>
                          <input
                            required
                            name="name"
                            type="text"
                            placeholder="Your full name"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Company
                          </label>
                          <input
                            name="company"
                            type="text"
                            placeholder="Company name"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Phone / WhatsApp
                          </label>
                          <input
                            name="phone"
                            type="tel"
                            placeholder="+971 50 123 4567"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Email *
                          </label>
                          <input
                            required
                            name="email"
                            type="email"
                            placeholder="you@company.com"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Location
                          </label>
                          <input
                            name="location"
                            type="text"
                            placeholder="City, country"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Elevator / Escalator Brand
                          </label>
                          <input
                            name="brand"
                            type="text"
                            placeholder="Brand name"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Part Number (optional)
                          </label>
                          <input
                            name="part_number"
                            type="text"
                            placeholder="Part number if known"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Service required *
                          </label>
                          <select
                            required
                            name="service"
                            defaultValue=""
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none appearance-none cursor-pointer"
                          >
                            <option value="" disabled>
                              Select service…
                            </option>
                            {inquiryTypes.map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="mt-5">
                        <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                          Requirement details *
                        </label>
                        <textarea
                          required
                          name="message"
                          maxLength={500}
                          rows={5}
                          onChange={(e) => setCharCount(e.target.value.length)}
                          placeholder="Describe your requirement, technical issue, or spare part need in detail…"
                          className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40 resize-none"
                        />
                        <div className="text-xs text-crest-deep/50 mt-1 flex justify-between">
                          <span>Max 500 characters.</span>
                          <span>{charCount}/500</span>
                        </div>
                      </div>

                      {error && (
                        <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                          {error}
                        </div>
                      )}

                       <div className="mt-6">
                        <ReCAPTCHA
                            sitekey={SITE_KEY}
                            onChange={(token: SetStateAction<string>) => {
                                setCaptchaToken(token);
                                if (error === "Please complete the CAPTCHA.") {
                                    setError("");
                                }
                            }}
                            onExpired={() => {
                                setCaptchaToken(null);
                            }}
                            onErrored={() => {
                              setCaptchaToken(null);
                          }}
                        />
                    </div>                      

                  <div className="mt-6 flex flex-wrap gap-4">
                      <button
                        type="submit"
                        disabled={status === "loading" || !captchaToken}
                        className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-crest-orange text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-crest-orangeLight transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                      >
                        {status === "loading" ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Sending…
                          </>
                        ) : (
                          <>
                            SUBMIT REQUIREMENT
                            <i className="ri-arrow-right-up-line"></i>
                          </>
                        )}
                      </button>
                        <a
                            href={BrochurePdf}
                            target="_blank"
                            rel="noopener noreferrer"
                           className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-crest-orange text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-crest-orangeLight transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                      >
                            <i className="ri-file-pdf-line"></i>
                            VIEW Catelog
                          </a>
                    </div>
                    </>
                  )}
                </form>
              </FadeUp>

              {/* Sidebar */}
              <Stagger className="lg:col-span-4 space-y-5" stagger={0.1}>
                <StaggerItem className="bg-crest-deep rounded-xl p-6 text-white">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-crest-orange text-white">
                      <i className="ri-phone-line text-xl"></i>
                    </span>
                    <h3 className="font-display text-xl text-crest-orange">
                      Technical Support
                    </h3>
                  </div>
                  <p className="text-white/75 text-sm leading-relaxed mt-4">
                    Call us directly for urgent technical support requirements.
                  </p>
                  <a
                    href="tel:0505664336"
                    className="mt-3 block text-white/75 hover:text-crest-orange text-lg font-display cursor-pointer"
                  >
                    050 566 43 36
                  </a>
                </StaggerItem>

                <StaggerItem className="bg-crest-deep rounded-xl p-6 text-white">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-crest-orange text-white">
                      <i className="ri-mail-line text-xl"></i>
                    </span>
                    <h3 className="font-display text-xl text-crest-orange">
                      Spare Part Enquiry
                    </h3>
                  </div>
                  <a
                    href="mailto:upliftone1@gmail.com"
                    className="mt-3 block text-white/75 hover:text-crest-orange text-sm cursor-pointer"
                  >
                    upliftone1@gmail.com
                  </a>
                </StaggerItem>

                <StaggerItem className="bg-crest-orange rounded-xl p-6 text-white">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-crest-deep text-crest-orange">
                      <i className="ri-map-pin-line text-xl"></i>
                    </span>
                    <h3 className="font-display text-xl">Our Location</h3>
                  </div>
                  <p className="text-crest-deep/80 text-sm leading-relaxed mt-3">
                    Serving Abu Dhabi and the wider UAE.
                  </p>
                  <p className="mt-2 text-crest-deep/70 text-sm font-medium">
                    Abu Dhabi – UAE
                  </p>
                </StaggerItem>
              </Stagger>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="bg-white py-16 md:py-20 border-t border-black/5">
          <div className="px-6 md:px-12">
            <FadeUp className="max-w-2xl mb-8">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-crest-deep/60 uppercase">
                <span className="w-6 h-px bg-crest-deep/40" /> Find us
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-crest-deep leading-tight mt-5">
                Based in Abu Dhabi.
              </h2>
              <p className="text-crest-deep/70 mt-4">
                Serving elevator and escalator requirements across Abu Dhabi and the UAE.
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="rounded-xl overflow-hidden border border-black/5 h-[420px]">
                <iframe
                  title="Abu Dhabi location map"
                  src="https://www.google.com/maps?q=Abu+Dhabi,+UAE&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </FadeUp>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}